/**
 * 설치 연식 필터 — 특정 연식 + 기간 (DESIGN §8.10.2).
 *
 * **두 방식을 모두 둔다.** 범위만 있으면 "2017 과 2021 을 견주기"가 표현되지
 * 않고, 개별 선택만 있으면 "2020년 이전"이 클릭 대여섯 번이 된다. 둘은 **AND**
 * 로 걸리고, 그 사실을 화면에 적는다 — 두 컨트롤이 서로를 조용히 덮어쓰면
 * 사용자는 자기가 무엇을 걸었는지 모르게 된다.
 *
 * **입력 관행으로 판정된 연식도 고를 수 있다.** 회색 + 사유를 붙이되 막지는
 * 않는다 — "그 사업자 것만 따로 보기"는 정당한 조회다 (§8.10.1).
 */

import { useQuery } from '@tanstack/react-query'

import { fetchEnvelope } from '@/lib/api'
import { int } from '@/lib/fmt'
import type { Envelope, YearCohortsData } from '@/lib/types'
import { useFilters } from '@/stores/filters'
import './YearFilter.css'

export function YearFilter() {
  const filters = useFilters()

  // 판정은 모집단 전체 기준이라 필터를 태우지 않는다 (§8.10.1)
  const q = useQuery({
    queryKey: ['metrics/year_cohorts'],
    queryFn: ({ signal }) =>
      fetchEnvelope<YearCohortsData>('/metrics/year_cohorts', { mode: 'standard' }, {}, signal),
    staleTime: 10 * 60 * 1000,
  })
  const cohorts = (q.data as Envelope<YearCohortsData> | undefined)?.data.cohorts ?? []

  const toggle = (year: number) => {
    const on = filters.year.includes(year)
    filters.set({
      year: on ? filters.year.filter((y) => y !== year) : [...filters.year, year].sort((a, b) => a - b),
    })
  }

  const num = (raw: string): number | null => {
    const v = Number(raw)
    return raw === '' || !Number.isFinite(v) ? null : v
  }

  const bothOn = filters.year.length > 0 && (filters.yearFrom != null || filters.yearTo != null)

  return (
    <div className="yfil">
      <div className="yfil__field">
        <span className="yfil__label">특정 연식</span>
        <ul className="yfil__grid">
          {cohorts.map((c) => {
            const on = filters.year.includes(c.year)
            const aside = c.verdict !== 'cohort'
            return (
              <li key={c.year}>
                <label
                  className={`yfil__opt${on ? ' is-on' : ''}${aside ? ' is-aside' : ''}`}
                  title={c.reason || `충전기 ${int(c.n_chargers)}기 · 사업자 ${c.n_busi}곳`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(c.year)} />
                  <span className="yfil__year num">{c.year}</span>
                  <span className="yfil__n num">{int(c.n_chargers)}</span>
                  {/* 막지 않는다 — 그 사업자 것만 따로 보는 것은 정당한 조회다 */}
                  {aside && <span className="yfil__aside">별도</span>}
                </label>
              </li>
            )
          })}
          {cohorts.length === 0 && <li className="yfil__empty">연식 목록을 불러오는 중…</li>}
        </ul>
        {filters.year.length > 0 && (
          <button type="button" className="btn-text" onClick={() => filters.set({ year: [] })}>
            선택 해제
          </button>
        )}
      </div>

      <div className="yfil__field">
        <span className="yfil__label">기간</span>
        <div className="yfil__range">
          <input
            type="number"
            className="yfil__num"
            placeholder="처음"
            aria-label="연식 시작"
            value={filters.yearFrom ?? ''}
            onChange={(e) => filters.set({ yearFrom: num(e.target.value) })}
          />
          <span aria-hidden="true">~</span>
          <input
            type="number"
            className="yfil__num"
            placeholder="끝"
            aria-label="연식 끝"
            value={filters.yearTo ?? ''}
            onChange={(e) => filters.set({ yearTo: num(e.target.value) })}
          />
          {(filters.yearFrom != null || filters.yearTo != null) && (
            <button
              type="button"
              className="btn-text"
              onClick={() => filters.set({ yearFrom: null, yearTo: null })}
            >
              해제
            </button>
          )}
        </div>
      </div>

      {/* 두 컨트롤이 서로를 덮어쓰지 않는다는 것을 말해 준다 */}
      {bothOn && (
        <p className="yfil__and">
          둘 다 걸려 있습니다 — <strong>특정 연식 ∩ 기간</strong> 만 남습니다.
        </p>
      )}
      <p className="yfil__note">
        「별도」는 <strong>한 사업자가 그 연식을 거의 다 차지</strong>해 설비의 나이로 읽을 수
        없는 경우입니다. 고를 수는 있습니다.
      </p>
    </div>
  )
}
